import React, {useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Descriptions, Modal, Table, Tag} from 'antd';
import dayjs from 'dayjs';
import {getSpecificPushDetail} from '@/api/hrPush';
import {SPECIFIC_TYPES} from '../routineUtils';
import {formatApiTime} from '../utils';

const APPROVAL_STATUS = {
    0: {label: '待审批', color: 'orange'},
    1: {label: '已通过', color: 'green'},
    2: {label: '已驳回', color: 'red'},
};

function pushStatusTag(status) {
    if (status === 1) return <Tag color="green">成功</Tag>;
    if (status === 2) return <Tag color="red">失败</Tag>;
    return <Tag>未发送</Tag>;
}

function executionStatus(task, latestHistory) {
    if (latestHistory) {
        if (latestHistory.status === 2) return <Tag color="green">成功</Tag>;
        if (latestHistory.status === 4) return <Tag color="red">失败</Tag>;
        if (latestHistory.status === 3) return <Tag color="orange">部分失败</Tag>;
    }
    if (task.approvalStatus === 0) return <Tag color="orange">待审批</Tag>;
    if (task.approvalStatus === 2) return <Tag color="red">已驳回</Tag>;
    if (task.pushDate && !task.lastExecuteTime) return <Tag color="blue">待发送</Tag>;
    return <Tag>{task.lastExecuteTime ? '已执行' : '-'}</Tag>;
}

export default function SpecificPushDetailModal({appId, task, onClose}) {
    const [data, setData] = useState();
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({current: 1, size: 10, total: 0});

    const load = useCallback(
        async (current = 1) => {
            if (!task) return;
            setLoading(true);
            try {
                const {data: detail} = await getSpecificPushDetail(appId, task.id, {
                    current,
                    size: pagination.size,
                });
                setData(detail);
                setPagination(old => ({
                    ...old,
                    current,
                    total: detail?.targets?.total || 0,
                }));
            } finally {
                setLoading(false);
            }
        },
        [appId, pagination.size, task]
    );

    useEffect(() => {
        if (task) load(1);
    }, [load, task]);

    const detail = data || {};
    const currentTask = detail.task || task || {};
    const latestHistory = detail.latestHistory;
    const targets = detail.targets || {};

    const columns = [
        {
            title: '序号',
            width: 70,
            render: (_, __, index) => (pagination.current - 1) * pagination.size + index + 1,
        },
        {title: '姓名', dataIndex: 'userName', render: value => value || '-'},
        {title: '员工号', dataIndex: 'employeeNumber'},
        {title: '职务', dataIndex: 'position', render: value => value || '-'},
        ...(currentTask.taskType === 'ANNUAL_LEAVE'
            ? [
                  {
                      title: '截止日期',
                      dataIndex: 'untilDate',
                      render: value => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
                  },
              ]
            : []),
        ...(currentTask.taskType === 'POINTS_REMINDER'
            ? [
                  {
                      title: '积分截止日',
                      dataIndex: 'pointsExpireDate',
                      render: value => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
                  },
                  {
                      title: '剩余积分',
                      dataIndex: 'remainingPoints',
                      render: value => (value == null ? '-' : value),
                  },
              ]
            : []),
        ...(currentTask.taskType === 'REFERRAL_AWARD'
            ? [
                  {title: '推荐人员工号', dataIndex: 'referEmployeeNumber'},
                  {
                      title: '奖金',
                      dataIndex: 'bonus',
                      render: value => (value == null ? '-' : value),
                  },
              ]
            : []),
        {
            title: '推送状态',
            dataIndex: 'pushStatus',
            width: 100,
            render: pushStatusTag,
        },
        {
            title: '推送时间',
            dataIndex: 'pushTime',
            width: 170,
            render: formatApiTime,
        },
        {
            title: '失败原因',
            dataIndex: 'errorMessage',
            render: value => value || '-',
        },
    ];

    return (
        <Modal
            open={!!task}
            title="特定推送详情"
            footer={null}
            width={960}
            onCancel={onClose}
            destroyOnClose
        >
            <Descriptions size="small" bordered column={2} style={{marginBottom: 16}}>
                <Descriptions.Item label="任务名称">
                    {currentTask.taskName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="任务类型">
                    {SPECIFIC_TYPES[currentTask.taskType] || currentTask.taskType || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="消息模板">
                    {currentTask.templateName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="创建人">
                    {currentTask.creatorName || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="发送人数">
                    {currentTask.targetCount || 0}
                </Descriptions.Item>
                <Descriptions.Item label="成功人数">
                    {currentTask.successCount || 0}
                </Descriptions.Item>
                <Descriptions.Item label="发送时间">
                    {currentTask.pushDate
                        ? `${dayjs(currentTask.pushDate).format('YYYY-MM-DD')} ${
                              currentTask.executeTime || ''
                          }`
                        : '立即发送'}
                </Descriptions.Item>
                <Descriptions.Item label="最后执行时间">
                    {formatApiTime(currentTask.lastExecuteTime) || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="执行状态">
                    {executionStatus(currentTask, latestHistory)}
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">
                    {formatApiTime(currentTask.createTime) || '-'}
                </Descriptions.Item>
                {currentTask.taskType === 'OFFBOARDING' && (
                    <Descriptions.Item label="审批状态">
                        {APPROVAL_STATUS[currentTask.approvalStatus] ? (
                            <Tag color={APPROVAL_STATUS[currentTask.approvalStatus].color}>
                                {APPROVAL_STATUS[currentTask.approvalStatus].label}
                            </Tag>
                        ) : (
                            '-'
                        )}
                    </Descriptions.Item>
                )}
                {currentTask.taskType === 'OFFBOARDING' && (
                    <Descriptions.Item label="审批意见" span={2}>
                        {currentTask.approvalOpinion || '-'}
                    </Descriptions.Item>
                )}
                {(currentTask.errorMessage || latestHistory?.errorMessage) && (
                    <Descriptions.Item label="错误信息" span={2}>
                        {currentTask.errorMessage || latestHistory.errorMessage}
                    </Descriptions.Item>
                )}
            </Descriptions>
            {currentTask.taskType === 'OFFBOARDING' && currentTask.approvalStatus === 0 && (
                <Alert
                    type="warning"
                    showIcon
                    message="该离职推送正在等待审批，审批通过后才会发送。"
                    style={{marginBottom: 16}}
                />
            )}
            <Table
                rowKey="id"
                size="small"
                columns={columns}
                dataSource={targets.records || []}
                loading={loading}
                scroll={{
                    x:
                        currentTask.taskType === 'REFERRAL_AWARD'
                            ? 880
                            : currentTask.taskType === 'POINTS_REMINDER'
                            ? 860
                            : 700,
                }}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.size,
                    total: pagination.total,
                    showTotal: total => `共 ${total} 条`,
                    onChange: load,
                }}
            />
        </Modal>
    );
}

SpecificPushDetailModal.propTypes = {
    appId: PropTypes.string.isRequired,
    task: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};

SpecificPushDetailModal.defaultProps = {
    task: null,
};
